import React, { useState, useRef, useEffect } from 'react';
import { IoArrowBack } from 'react-icons/io5';

const ProspectsHeader = (props) => {
    const {
        campaignName, autopilotStatus, onBack, onRefresh, onViewSummary, onMailboxClick,
        onUpdateCampaign, onAddMatchingCompany, onAddContact, onPromptConfig, onSetupMeeting,
        onGenerateSelected, onGenerateWithPrompt, onSendEmailSelected, onSendLinkedinSelected, onDeleteSelected,
        onStartAutopilot, onStopAutopilot, onSetViewCount,
    } = props;

    // --- State & Refs for dropdowns now live inside the header ---
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [showConfigMenu, setShowConfigMenu] = useState(false);
    const [showProspectMenu, setShowProspectMenu] = useState(false);

    const actionMenuRef = useRef(null);
    const configMenuRef = useRef(null);
    const prospectMenuRef = useRef(null);

    // --- Effect to handle clicking outside of the dropdowns to close them ---
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) setShowActionMenu(false);
            if (configMenuRef.current && !configMenuRef.current.contains(event.target)) setShowConfigMenu(false);
            if (prospectMenuRef.current && !prospectMenuRef.current.contains(event.target)) setShowProspectMenu(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
                <button onClick={onBack} className="p-2 text-slate-600 bg-white border border-slate-300 rounded-full hover:bg-slate-100 transition-colors" aria-label="Back">
                    <IoArrowBack size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">{campaignName}</h1>
                    <p className="text-sm text-slate-500">Prospect List</p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <button onClick={onRefresh} className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white rounded-md border border-slate-300 hover:bg-slate-50 transition-all">Refresh</button>
                <button onClick={onViewSummary} className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white rounded-md border border-slate-300 hover:bg-slate-50 transition-all">Summary</button>
                <button onClick={onMailboxClick} className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white rounded-md border border-slate-300 hover:bg-slate-50 transition-all">Unibox</button>

                {/* Config Dropdown */}
                <div className="relative" ref={configMenuRef}>
                    <button onClick={() => setShowConfigMenu(s => !s)} className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white rounded-md border border-slate-300 hover:bg-slate-50 transition-all">Config ▼</button>
                    {showConfigMenu && (
                        <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-50 py-1">
                            <a onClick={() => { onUpdateCampaign(); setShowConfigMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Update Campaign</a>
                            <a onClick={() => { onAddMatchingCompany(); setShowConfigMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Add Matching Company</a>
                            <a onClick={() => { onAddContact(); setShowConfigMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Add Company Manual</a>
                            <a onClick={() => { onPromptConfig(); setShowConfigMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Prompt Config</a>
                            <a onClick={() => { onSetupMeeting(); setShowConfigMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Setup Meeting</a>
                        </div>
                    )}
                </div>

                {/* Action Dropdown */}
                <div className="relative" ref={actionMenuRef}>
                    <button onClick={() => setShowActionMenu(s => !s)} className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white rounded-md border border-slate-300 hover:bg-slate-50 transition-all">Action ▼</button>
                    {showActionMenu && (
                        <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-50 py-1">
                            <a onClick={() => { onGenerateSelected(); setShowActionMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Generate</a>
                            <a onClick={() => { onGenerateWithPrompt(); setShowActionMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Generate with Prompt</a>
                            <a onClick={() => { onSendEmailSelected(); setShowActionMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Send Email</a>
                            <a onClick={() => { onSendLinkedinSelected(); setShowActionMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">Send Linkedin</a>
                            <div className="my-1 h-px bg-slate-100"></div>
                            <a onClick={() => { onDeleteSelected(); setShowActionMenu(false); }} className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 hover:text-red-700 cursor-pointer">Delete</a>
                        </div>
                    )}
                </div>
                
                <button onClick={autopilotStatus ? onStopAutopilot : onStartAutopilot} className={`px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${autopilotStatus ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                    {autopilotStatus ? 'Stop AutoPilot' : 'Start AutoPilot'}
                </button>

                {/* View Dropdown */}
                <div className="relative" ref={prospectMenuRef}>
                    <button onClick={() => setShowProspectMenu(s => !s)} className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white rounded-md border border-slate-300 hover:bg-slate-50 transition-all">View ▼</button>
                    {showProspectMenu && (
                        <div className="absolute top-full right-0 mt-2 w-20 bg-white border border-slate-200 rounded-md shadow-lg z-50 py-1">
                            {[10, 20, 50, 100].map(num => (
                                <a key={num} onClick={() => { onSetViewCount(num); setShowProspectMenu(false); }} className="block w-full px-4 py-2 text-center text-sm text-slate-700 hover:bg-slate-100 cursor-pointer">{num}</a>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProspectsHeader;